import React, { useEffect, useState, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { FormattedMessage } from 'react-intl';

import ContentBox from '../../components/layout/ContentBox';
import DLFrame from '../../components/digitalTrial/DLFrame';
import DigitalCaseNavigator from '../../components/digitalTrial/DigitalCaseNavigatior';
import useFullscreenStatus from '../../hooks/useFullscreenStatus';
import { checkTrialHasExpired } from '../../components/utilities/TrialUtility';

import { Enrollment, RootState } from '../../redux/types';
import {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  setSelectedCase,
  fetchUsersDigitalSubmissions,
  addOrUpdateDigitalSubmission,
  fetchDigitalSubmissionMeta,
  finalizeDigitalSubmissionDRT,
  finalizeContextSubmission,
  fetchDigitalExerciseGroundTruths,
  fetchTrial,
  navigateTo,
} from '../../redux/actions';
import {
  selectDigitalCases,
  selectSelectedCase,
  selectDigitalSubmissionByQuestionAndCase,
  selectSelectedSection,
  selectDigitalGTDictionaryByQuestionAndCase,
  selectEmptySubmissionCount,
  selectDigitalSubmissionMetaBySection,
  selectContextQuestionsDRT,
  selectDigitalSubmissions,
  selectNormalQuestionsDRT,
} from '../../redux/reducers/SurveyDataReducer';
import DigitalTimer from '../../components/digitalTrial/DigitalTimer';
import GenericDialog from '../../components/GenericDialog';
import AnswerSummary from '../../components/digitalTrial/AnswerSummary';
import EvaluationResult from '../../components/digitalTrial/EvaluationResult';
import DigitalContextQuestions from '../../components/digitalTrial/DigitalContextQuestions';
import QuestionInputContainer from '../../components/questions/QuestionInputContainer';
import ApiClient from '../../loopback/apiClient';
import ColorContext from '../../context/ColorContext';

const FullscreenContainer = styled('div')`
  overflow: auto;
  background-color: white;
`;

type MatchParams = {
  trialId: string;
  surveyId: string;
  antibodyId: string;
};

type Props = PropsFromRedux;

const DigitalQuestionnaireView: React.FC<Props> = ({
  digitalCases,
  selectedCase,
  digitalQuestions,
  digitalContextQuestions,
  digitalSurvey,
  selectedSection,
  digitalSubmissions,
  submissionsMapByQuestionAndCaseId,
  digitalSubmissionMeta,
  digitalGroundTruths,
  emptySubmissionCount,
  trial,
  fetchFullDigitalSurvey: fetchFullDigitalSurveyAction,
  setSelectedDigitalsection: setSelectedDigitalSectionAction,
  setSelectedCase: setSelectedCaseAction,
  fetchUsersDigitalSubmissions: fetchUsersDigitalSubmissionsAction,
  addOrUpdateDigitalSubmission: addOrUpdateDigitalSubmissionAction,
  fetchDigitalSubmissionMeta: fetchDigitalSubmissionMetaAction,
  finalizeDigitalSubmissionDRT: finalizeDigitalSubmissionAction,
  finalizeContextSubmission: finalizeContextSubmissionAction,
  fetchDigitalExerciseGroundTruths: fetchDigitalExerciseGroundTruthsAction,
  fetchTrial: fetchTrialAction,
}: Props) => {
  const navigate = useNavigate();
  const params = useParams<MatchParams>();
  const { trialId, surveyId, antibodyId } = params;
  const [enrollment, setEnrollment] = useState<Enrollment>();
  useEffect(() => {
    fetchFullDigitalSurveyAction(Number(surveyId));
    setSelectedDigitalSectionAction(Number(antibodyId));
    fetchUsersDigitalSubmissionsAction(Number(trialId));
    fetchDigitalSubmissionMetaAction(Number(trialId));
    fetchTrialAction(Number(trialId));
    ApiClient.getUserEnrollmentByTrial(Number(trialId)).then((e) => setEnrollment(e));
  }, [
    surveyId,
    antibodyId,
    trialId,
    fetchFullDigitalSurveyAction,
    setSelectedDigitalSectionAction,
    fetchUsersDigitalSubmissionsAction,
    fetchDigitalSubmissionMetaAction,
    fetchTrialAction,
  ]);

  const fullscreenRef = useRef<HTMLDivElement>(null);
  const isFullscreen = useFullscreenStatus();

  const [promptFinalize, setPromptFinalize] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [showContextQuestions, setShowContextQuestions] = useState<boolean>(false);
  const [hideAnnotation, setHideAnnotation] = useState<boolean>(true);
  const [trialHasExpired, setTrialHasExpired] = useState<boolean>(false);

  const contextQuestionsExist = digitalContextQuestions.length > 0;
  useEffect(() => {
    if (digitalCases.length) {
      setSelectedCaseAction(digitalCases[0].id);
    }
  }, [digitalCases, setSelectedCaseAction]);

  useEffect(() => {
    if (digitalSurvey && digitalSurvey.exercise) {
      fetchDigitalExerciseGroundTruthsAction(digitalSurvey.id);
    }
  }, [digitalSurvey, fetchDigitalExerciseGroundTruthsAction]);

  useEffect(() => {
    setTrialHasExpired(checkTrialHasExpired(trial));
  }, [trial]);

  const finalizeHandler = () => {
    setPromptFinalize(true);
  };

  const onFinalizeConfirm = (confirm: boolean) => {
    if (confirm) {
      finalizeDigitalSubmissionAction(Number(trialId), Number(antibodyId));
    }
    setPromptFinalize(false);
  };

  const onShowResults = () => {
    if (contextQuestionsExist && !digitalSubmissionMeta?.ctxQuestionSubmitted) {
      setShowContextQuestions(true);
    } else {
      setShowResults(true);
    }
  };

  const onHomeClick = () => {
    navigateTo(`/digitalTrial/${trialId}/menu`, navigate);
  };

  const onRequestFullScreen = async () => {
    const fsElement = fullscreenRef.current;
    if (fsElement) {
      await fsElement.requestFullscreen();
    }
  };

  if (!digitalSubmissionMeta?.submissionFinalized && trialHasExpired && !digitalSurvey?.exercise) {
    finalizeDigitalSubmissionAction(Number(trialId), Number(antibodyId));
  }

  return (
    <ColorContext.Provider value="tertiary">
      <ContentBox title={`Digital Trial ${digitalSurvey?.exercise ? 'Exercise' : 'Questionnaire'}`} elevation={0} color="tertiary">
        <FullscreenContainer ref={fullscreenRef}>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }} gap={1}>
            <Box flexGrow={1}>
              {
                !isFullscreen && (
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="textSecondary" to={`/digitalTrial/${trialId}/menu`}>Menu</Link>
                    <Link color="textPrimary" to={`/digitaltrial/${trialId}/survey/${surveyId}/antibodies`}>
                      {digitalSurvey?.exercise ? 'Exercises' : 'Antibodies'}
                    </Link>
                    <Link color="textPrimary" to={`/digitaltrial/${trialId}/survey/${surveyId}/antibody/${antibodyId}/form`}>{selectedSection?.name}</Link>
                  </Breadcrumbs>
                )
              }
            </Box>
            {
              !digitalSurvey?.exercise && digitalSurvey?.time && (
                <DigitalTimer
                  trialId={Number(trialId)}
                  surveyId={Number(surveyId)}
                  sectionId={Number(antibodyId)}
                  finalized={digitalSubmissionMeta?.submissionFinalized}
                />
              )
            }
            <Box display="flex">
              {selectedCase?.url && (
                <Paper elevation={0}>
                  <Alert severity="info">
                    Please click on this&nbsp;
                    <MuiLink href="#" onClick={() => window.open(`${selectedCase?.url}?embeddedmode=true&skipacceptapplicationpolicy=true&hideallannotations=${hideAnnotation}&disableswitchlabel=true`, '_blank')}>
                      link
                    </MuiLink>
                    &nbsp; if no digital image is displayed.
                  </Alert>
                </Paper>
              )}
              {[
                isFullscreen && (
                  <Tooltip key="expandexitbutton" title="Exit Fullscreen Mode">
                    <IconButton color="tertiary" onClick={() => document.exitFullscreen()}>
                      <FullscreenExitIcon />
                    </IconButton>
                  </Tooltip>
                ),
                !isFullscreen && (
                  <Tooltip key="expandbutton" title="Enter Fullscreen Mode">
                    <IconButton color="tertiary" onClick={onRequestFullScreen}><FullscreenIcon /></IconButton>
                  </Tooltip>
                ),
              ]}
            </Box>
          </Box>
          <DLFrame
            frameSrc={selectedCase?.url}
            hideAnnotation={digitalSurvey?.exercise ? hideAnnotation : true}
          />
          {
            digitalCases && digitalCases.length !== 0 && selectedCase !== undefined && (
              <DigitalCaseNavigator
                digitalCases={digitalCases}
                selectedCase={selectedCase.id}
                onCaseSelect={setSelectedCaseAction}
              />
            )
          }
          {
            selectedCase && enrollment && (
              <QuestionInputContainer
                trialId={Number(trialId)}
                enrollmentId={enrollment.id}
                digitalQuestions={digitalQuestions}
                digitalCaseId={selectedCase.id}
                digitalSectionId={Number(antibodyId)}
                submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
                addOrUpdateDigitalSubmission={addOrUpdateDigitalSubmissionAction}
                disabled={!!digitalSubmissionMeta?.submissionFinalized && !digitalSurvey?.exercise}
                exerciseGroundTruth={digitalSurvey?.exercise ? digitalGroundTruths : undefined}
                setHideAnnotation={setHideAnnotation}
                hideActionButtons={isFullscreen}
              />
            )
          }
          {
            digitalSurvey && !digitalSurvey.exercise && !isFullscreen && (
              <Box display="flex" justifyContent="flex-start" sx={{ m: 2 }} aria-label="survey actions">
                {
                  digitalSubmissionMeta?.submissionFinalized && (
                    <Button color="tertiary" sx={{ ml: 2 }} onClick={onShowResults} variant="contained">Show Results</Button>
                  )
                }
                <Button color="tertiary" sx={{ ml: 2 }} disabled={digitalSubmissionMeta?.submissionFinalized} onClick={finalizeHandler} variant="contained">Finalize Submission</Button>
                <Button color="tertiary" variant="outlined" sx={{ ml: 2 }} onClick={onHomeClick}>Home</Button>
              </Box>
            )
          }
        </FullscreenContainer>
      </ContentBox>
      <GenericDialog
        maxWidth="lg"
        isOpen={promptFinalize}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ pb: 2 }}>
            <Typography sx={{ p: 2 }} variant="h5" component="div">
              Complete the Digital Readout Test
            </Typography>
            <Typography component="p" gutterBottom sx={{ ml: 2 }}>
              Are you sure you want to submit and finalize the Digital Readout Test?
              Subsequent changes to your submission will then no longer be possible.
            </Typography>
            {
              emptySubmissionCount > 0 && (
                <Typography component="p" gutterBottom sx={{ ml: 2 }} color="secondary">
                  N/A represents that you did not enter any answer for the provided question.
                  If it is not intentional, you can still go back and answer before finalizing.
                </Typography>
              )
            }
          </Box>
          <Typography variant="h6" sx={{ my: 2, ml: 2 }}><FormattedMessage id="answerSummary.title" /></Typography>
          <AnswerSummary
            digitalCases={digitalCases}
            digitalQuestions={digitalQuestions}
            submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" sx={{ m: 2 }}>
          <Button variant="contained" color="secondary" sx={{ ml: 2 }} onClick={() => onFinalizeConfirm(false)}>Back</Button>
          <Button color="tertiary" variant="contained" sx={{ ml: 2 }} onClick={() => onFinalizeConfirm(true)}>Finalize</Button>
        </Box>
      </GenericDialog>
      <GenericDialog
        maxWidth="md"
        isOpen={showResults}
        onClose={() => setShowResults(false)}
        onRequestClose={() => setShowResults(false)}
      >
        <Box style={{ marginBottom: 10 }}>
          <EvaluationResult
            digitalSection={selectedSection}
            digitalSubmissionMeta={digitalSubmissionMeta}
          />
          <Divider style={{ display: 'none' }} variant="middle" />
        </Box>
      </GenericDialog>
      <GenericDialog
        maxWidth="lg"
        isOpen={showContextQuestions}
        onClose={() => setShowContextQuestions(false)}
        onRequestClose={() => setShowContextQuestions(false)}
      >
        <Box style={{ marginBottom: 10 }}>
          <DigitalContextQuestions
            contextQuestions={digitalContextQuestions}
            digitalSubmissions={digitalSubmissions}
            sectionId={Number(antibodyId)}
            trialId={Number(trialId)}
            enrollmentId={enrollment?.id}
            addOrUpdateDigitalSubmission={addOrUpdateDigitalSubmissionAction}
            finalizeContextSubmissionAction={finalizeContextSubmissionAction}
            onAfterFinalize={() => { setShowContextQuestions(false); setShowResults(true); }}
          />
        </Box>
      </GenericDialog>
    </ColorContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  digitalSurvey: state.SurveyData.survey,
  digitalSubmissionMeta: selectDigitalSubmissionMetaBySection(state),
  selectedCase: selectSelectedCase(state),
  digitalCases: selectDigitalCases(state),
  digitalQuestions: selectNormalQuestionsDRT(state),
  digitalContextQuestions: selectContextQuestionsDRT(state),
  selectedSection: selectSelectedSection(state),
  digitalSubmissions: selectDigitalSubmissions(state),
  submissionsMapByQuestionAndCaseId: selectDigitalSubmissionByQuestionAndCase(state),
  digitalGroundTruths: selectDigitalGTDictionaryByQuestionAndCase(
    state, state.SurveyData.digitalSections.selectedSection,
  ),
  emptySubmissionCount: selectEmptySubmissionCount(state),
  trial: state.trial.trial,
});

const dispatchProps = {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  setSelectedCase,
  fetchUsersDigitalSubmissions,
  addOrUpdateDigitalSubmission,
  fetchDigitalSubmissionMeta,
  finalizeDigitalSubmissionDRT,
  finalizeContextSubmission,
  fetchDigitalExerciseGroundTruths,
  fetchTrial,
};

const connector = connect(mapStateToProps, dispatchProps);

type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DigitalQuestionnaireView);
